
/**
 * Módulo de impresión para reportes - Coordinador principal
 * Este módulo centraliza la lógica de impresión, captura y generación de PDFs
 */
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

import ReportConfigManager from './modules/ReportConfigManager.js';
import UIManager from './modules/UIManager.js';
import MembreteManager from './modules/MembreteManager.js';
import CaptureManager from './modules/CaptureManager.js';
import TextUtils from './modules/TextUtils.js';
import PDFGenerator from './modules/PDFGenerator.js';
import ShareManager from './modules/ShareManager.js';
import ModalManager from './modules/ModalManager.js';
import ProcessManager from './modules/ProcessManager.js';
import TableDataExtractor from './modules/TableDataExtractor.js';

class PrintReportModule {
    constructor(options = {}) {
        this.options = {
            reportContainer: options.reportContainer || '.report-content',
            captureQuality: options.captureQuality || 0.95,
            pdfFormat: options.pdfFormat || 'A4',
            pdfOrientation: options.pdfOrientation || 'landscape',
            includeCharts: options.includeCharts !== false,
            includeTable: options.includeTable !== false,
            includeMap: options.includeMap !== false,
            preserveLayout: options.preserveLayout !== false,
            useNativeTables: options.useNativeTables !== false,
            maxRowsPerPage: options.maxRowsPerPage || 25,
            tableFontSize: options.tableFontSize || 8,
            onlyTableExport: options.onlyTableExport || false,
            ...options
        };

        // ✅ INICIALIZAR MÓDULOS
        this.configManager = new ReportConfigManager();
        this.uiManager = new UIManager();
        this.membreteManager = new MembreteManager();
        this.captureManager = new CaptureManager(this.options);
        this.pdfGenerator = new PDFGenerator(this.options);
        this.shareManager = new ShareManager();
        this.modalManager = new ModalManager(this);
        this.processManager = new ProcessManager(this);
        this.tableExtractor = new TableDataExtractor(this.options);

        // ✅ INYECTAR DEPENDENCIAS
        this.tableExtractor.setPrintModule(this);
        this.pdfGenerator.setMembreteManager(this.membreteManager);

        this.capturedImages = {
            charts: [],
            table: null,
            map: null,
            sections: []
        };

        this.extractedTableData = {
            headers: [],
            rows: [],
            title: 'Tabla de Datos'
        };

        this.init();
    }

    init() {
        this.createPrintInterface();
    }

    /**
     * ✅ MANTENER: Crear interfaz de impresión (específico del proyecto)
     */
    createPrintInterface() {
        const possibleContainers = [
            '.print-report',
            // '.report-actions',
            // '.card-header',
            // '.d-flex.justify-content-between',
            // '.page-header',
            // '.toolbar',
            // '.actions-container',
            // 'header',
            // '.top-bar'
        ];

        let actionsContainer = null;

        for (const selector of possibleContainers) {
            actionsContainer = document.querySelector(selector);
            if (actionsContainer) {
                //console.log(`🎯 Contenedor encontrado: ${selector}`);
                break;
            }
        }

        if (!actionsContainer || document.getElementById('btn-print-report')) {
            return;
        }

        const printBtn = document.createElement('button');
        printBtn.id = 'btn-print-report';
        printBtn.className = 'btn w-100 btn-primary';
        printBtn.title = 'Generar PDF del reporte';
        printBtn.innerHTML = '<i class="fas fa-print me-1"></i> Imprimir';

        const existingButtons = actionsContainer.querySelectorAll('button');
        if (existingButtons.length > 0) {
            const lastButton = existingButtons[existingButtons.length - 1];
            lastButton.parentNode.insertBefore(printBtn, lastButton.nextSibling);
        } else {
            actionsContainer.appendChild(printBtn);
        }

        printBtn.addEventListener('click', () => this.showPrintDialog());
        //console.log('✅ Botón de impresión agregado universalmente');
    }

    // ✅ MÉTODOS DELEGADOS CORRECTAMENTE (MANTENER)
    detectReportType() {
        return this.configManager.detectReportType(this.options);
    }

    getModalTitle(reportType) {
        return this.configManager.getModalTitle(reportType);
    }

    getDefaultTitle(reportType) {
        return this.configManager.getDefaultTitle(reportType);
    }

    getChartsLabel(reportType) {
        return this.configManager.getChartsLabel(reportType);
    }

    getTableLabel(reportType) {
        return this.configManager.getTableLabel(reportType);
    }

    isRecommendedLandscape(reportType) {
        return this.configManager.isRecommendedLandscape(reportType);
    }

    isRecommendedPortrait(reportType) {
        return this.configManager.isRecommendedPortrait(reportType);
    }

    showPrintDialog() {
        return this.modalManager.showPrintDialog();
    }

    showModal(modal) {
        return this.modalManager.showModal(modal);
    }

    hideModal(modal) {
        return this.modalManager.hideModal(modal);
    }

    showPrintPreview(pdfBlob, config) {
        return this.modalManager.showPrintPreview(pdfBlob, config);
    }

    getPrintConfiguration(modal) {
        return this.processManager.getPrintConfiguration(modal);
    }

    validatePrintConfig(config) {
        return this.processManager.validatePrintConfig(config);
    }

    async startPrintProcess(config) {
        return this.processManager.startPrintProcess(config);
    }

    showLoading(message) {
        return this.uiManager.showLoading(message);
    }

    hideLoading() {
        return this.uiManager.hideLoading();
    }

    showError(message) {
        return this.uiManager.showError(message);
    }

    showSuccess(message) {
        return this.uiManager.showSuccess(message);
    }

    updateLoadingMessage(message) {
        return this.uiManager.updateLoadingMessage(message);
    }

    showNotification(message, type = 'info') {
        return this.uiManager.showNotification(message, type);
    }

    async captureElement(element, type) {
        return await this.captureManager.captureElement(element, type);
    }

    isElementVisible(element) {
        return this.captureManager.isElementVisible(element);
    }

    async prepareElementForCapture(element) {
        return await this.captureManager.prepareElementForCapture(element);
    }

    async restoreElementAfterCapture(element) {
        return await this.captureManager.restoreElementAfterCapture(element);
    }

    async captureTable() {
        return await this.captureManager.captureTable();
    }

    hasVisualContent(element) {
        return this.captureManager.hasVisualContent(element);
    }

    hasTableOnly(element) {
        return this.captureManager.hasTableOnly(element);
    }

    isTableOnlyRow(element) {
        return this.captureManager.isTableOnlyRow(element);
    }

    async extractTableData() {
        return await this.tableExtractor.extractTableData();
    }

    async detectAndExtractTableData() {
        return await this.tableExtractor.detectAndExtractTableData();
    }

    async extractAlertaTableListTanstack() {
        return await this.tableExtractor.extractAlertaTableListTanstack();
    }

    async extractFromDOMStructure() {
        return await this.tableExtractor.extractFromDOMStructure();
    }

    async extractUniversalTableData() {
        return await this.tableExtractor.extractUniversalTableData();
    }

    async generateReportPDF(content, config) {
        return await this.pdfGenerator.generateReportPDF(content, config, this.membreteManager, TextUtils);
    }

    async sharePDF(fileName = null, reportType = null) {
        try {
            const detectedReportType = reportType || this.detectReportType();
            const result = await this.shareManager.sharePDF(fileName, detectedReportType);

            if (result.success) {
                console.log(`✅ PDF compartido: ${result.method}`);

                if (result.message) {
                    this.showNotification(result.message, 'success');
                }

                return result;
            } else {
                console.error('❌ Error compartiendo PDF:', result.error);
                this.showError(`Error al compartir: ${result.error}`);
                return result;
            }

        } catch (error) {
            console.error('❌ Error en sharePDF():', error);
            this.showError('Error al compartir el PDF');
            return {
                success: false,
                error: error.message
            };
        }
    }

    downloadPDF(fileName = null) {
        const reportType = this.detectReportType();
        const finalFileName = fileName || this.shareManager.generateFileName(reportType);
        return this.shareManager.downloadPDF(finalFileName);
    }

    printPDF() {
        return this.shareManager.printPDF();
    }

    setPDFForSharing(pdfBlob) {
        return this.shareManager.setPDF(pdfBlob);
    }

    async loadMembreteImage() {
        return await this.membreteManager.loadMembreteImage();
    }

    addMembreteAsBackground(pdf, membreteBase64) {
        return this.membreteManager.addMembreteAsBackground(pdf, membreteBase64);
    }

    getCurrentPrintConfiguration() {
        try {
            // 1. Intentar desde el processManager si está procesando
            if (this.processManager && this.processManager.currentConfig) {
                console.log(`📋 Orientación desde proceso actual: ${this.processManager.currentConfig.orientation}`);
                return this.processManager.currentConfig;
            }

            // 2. Intentar desde modal activo
            const modal = document.querySelector('#printReportModal.show, .modal.show');
            if (modal) {
                const orientation = modal.querySelector('#print-orientation')?.value;
                if (orientation) {
                    console.log(`📋 Orientación desde modal activo: ${orientation}`);
                    return { orientation };
                }
            }

            // 3. Fallback a configuración por defecto
            return this.getPrintConfigurationRobust();

        } catch (error) {
            console.error('❌ Error obteniendo configuración actual:', error);
            return { orientation: 'landscape' };
        }
    }

    async captureUniversalSections() {
        const sections = [];

        try {
            // 🎯 DETECTAR CONFIGURACIÓN
            const config = this.getReportConfiguration();
            const printConfig = this.getCurrentPrintConfiguration();
            const isHorizontalPrint = printConfig.orientation === 'landscape';

            console.log('📋 Configuración detectada:', config);
            console.log(`📏 Orientación: ${isHorizontalPrint ? 'HORIZONTAL' : 'VERTICAL'}`);

            // 🎯 DECIDIR ESTRATEGIA BASADA EN CONFIGURACIÓN
            if (config.type === 'manual') {
                // ✅ CONFIGURACIÓN MANUAL (RECORRIDOS, KM, etc.)
                return await this.captureWithManualConfig(config, isHorizontalPrint);
            } else {
                // ✅ CONFIGURACIÓN AUTOMÁTICA (ALERTAS)
                return await this.captureWithAutoDetection(isHorizontalPrint, sections);
            }

        } catch (error) {
            console.error('❌ Error en captura universal:', error);
            return [];
        }
    }

    /**
     * NUEVO: Obtener configuración del reporte
     */
    getReportConfiguration() {
        const options = this.options;

        // ✅ SI TIENE CONFIGURACIÓN MANUAL
        if (options.reportType && (options.captureTargets || options.horizontalRows)) {
            return {
                type: 'manual',
                reportType: options.reportType,
                horizontalStrategy: options.horizontalStrategy || 'targets',
                verticalStrategy: options.verticalStrategy || 'targets',
                captureTargets: options.captureTargets || [],
                horizontalRows: options.horizontalRows || []
            };
        }

        // ✅ CONFIGURACIÓN AUTOMÁTICA
        return {
            type: 'auto',
            reportType: 'auto-detected'
        };
    }

    /**
     * NUEVO: Captura con configuración manual
     */
    async captureWithManualConfig(config, isHorizontalPrint) {
        const sections = [];

        console.log(`🎯 Configuración MANUAL para ${config.reportType.toUpperCase()}`);

        if (isHorizontalPrint) {
            // ✅ ESTRATEGIA HORIZONTAL SEGÚN CONFIGURACIÓN
            const strategy = config.horizontalStrategy;

            if (strategy === 'rows' && config.horizontalRows.length > 0) {
                console.log('📊 HORIZONTAL: Capturando por filas configuradas');
                return await this.captureConfiguredRows(config.horizontalRows, sections);
            } else if (strategy === 'targets' && config.captureTargets.length > 0) {
                console.log('📊 HORIZONTAL: Capturando targets individuales');
                return await this.captureConfiguredTargets(config.captureTargets, sections);
            }
        } else {
            // ✅ ESTRATEGIA VERTICAL SEGÚN CONFIGURACIÓN
            const strategy = config.verticalStrategy;

            if (strategy === 'targets' && config.captureTargets.length > 0) {
                console.log('📱 VERTICAL: Capturando targets individuales');
                return await this.captureConfiguredTargets(config.captureTargets, sections);
            }
        }

        // ✅ FALLBACK
        console.warn('⚠️ Configuración manual incompleta, usando detección automática');
        return await this.captureWithAutoDetection(isHorizontalPrint, []);
    }

    /**
     * NUEVO: Captura con detección automática (ALERTAS)
     */
    async captureWithAutoDetection(isHorizontalPrint, sections) {
        console.log('🔄 Configuración AUTOMÁTICA - detección por DOM');

        // ✅ ANÁLISIS AUTOMÁTICO DE LA ESTRUCTURA
        const structure = this.captureManager.analyzeReportStructure();

        console.log(`📊 Estructura detectada automáticamente:`, structure);

        // 🎯 LÓGICA ESPECÍFICA PARA ALERTAS (PRESERVADA)
        if (structure.reportType === 'alertas') {
            console.log('🚨 Usando estrategia ESPECÍFICA para ALERTAS (automática)');
            return await this.captureAlertasSpecific(isHorizontalPrint, sections);
        } else {
            // ✅ SISTEMA GENÉRICO PARA OTROS REPORTES SIN CONFIGURACIÓN
            console.log(`🔄 Usando estrategia GENÉRICA para ${structure.reportType.toUpperCase()}`);
            return await this.captureUniversalAutoGeneric(structure, isHorizontalPrint, sections);
        }
    }

    /**
     * NUEVO: Capturar filas configuradas manualmente
     */
    async waitForGoogleMapsToLoad(element) {
        console.log('🗺️ Esperando a que Google Maps esté completamente cargado...');

        let attempts = 0;
        const maxAttempts = 30; // Más tiempo para mapas

        while (attempts < maxAttempts) {
            try {
                // ✅ BUSCAR CONTENEDORES DE GOOGLE MAPS
                const mapContainers = element.querySelectorAll(
                    '.gm-style, [class*="google-map"], [id*="mapa-"], [id*="map-"]'
                );

                if (mapContainers.length === 0) {
                    console.log('📍 No se encontraron mapas, continuando...');
                    return true;
                }

                let allMapsReady = true;

                for (const mapContainer of mapContainers) {
                    // ✅ VERIFICAR QUE EL CONTENEDOR TENGA DIMENSIONES
                    const rect = mapContainer.getBoundingClientRect();
                    if (rect.width <= 10 || rect.height <= 10) {
                        console.log(`📐 Mapa sin dimensiones: ${rect.width}x${rect.height} (intento ${attempts + 1})`);
                        allMapsReady = false;
                        break;
                    }

                    // ✅ VERIFICAR CANVAS INTERNOS
                    const canvases = mapContainer.querySelectorAll('canvas');
                    if (canvases.length === 0) {
                        console.log(`🖼️ Mapa sin canvas (intento ${attempts + 1})`);
                        allMapsReady = false;
                        break;
                    }

                    // ✅ VERIFICAR QUE LOS CANVAS TENGAN DIMENSIONES VÁLIDAS
                    for (const canvas of canvases) {
                        if (canvas.width <= 0 || canvas.height <= 0) {
                            console.log(`🖼️ Canvas con dimensiones inválidas: ${canvas.width}x${canvas.height} (intento ${attempts + 1})`);
                            allMapsReady = false;
                            break;
                        }
                    }

                    if (!allMapsReady) break;

                    // ✅ VERIFICAR QUE NO HAYA SPINNERS O ELEMENTOS DE CARGA
                    const loadingElements = mapContainer.querySelectorAll(
                        '.gm-spinner, [class*="loading"], [class*="spinner"]'
                    );
                    if (loadingElements.length > 0) {
                        console.log(`⏳ Mapa aún cargando (intento ${attempts + 1})`);
                        allMapsReady = false;
                        break;
                    }
                }

                if (allMapsReady) {
                    console.log(`✅ Todos los Google Maps están listos después de ${attempts + 1} intentos`);
                    // ✅ ESPERA ADICIONAL PARA ESTABILIDAD
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    return true;
                }

            } catch (error) {
                console.warn(`⚠️ Error verificando Google Maps (intento ${attempts + 1}):`, error);
            }

            await new Promise(resolve => setTimeout(resolve, 500));
            attempts++;
        }

        console.warn(`⚠️ Google Maps no se completó en ${maxAttempts} intentos, continuando...`);
        return false;
    }

    /**
     * MEJORAR: Captura de filas con espera específica para Google Maps
     */
    async captureConfiguredRows(horizontalRows, sections) {
        try {
            console.log(`📊 Capturando ${horizontalRows.length} filas configuradas`);

            for (let i = 0; i < horizontalRows.length; i++) {
                const rowConfig = horizontalRows[i];
                console.log(`📸 Intentando capturar fila: ${rowConfig.title}`);

                let rowElement = null;

                if (rowConfig.selectorMethod === 'byElement') {
                    const targetElement = document.querySelector(rowConfig.selector);
                    if (targetElement) {
                        rowElement = targetElement.closest('.row');
                    }
                } else {
                    rowElement = document.querySelector(rowConfig.selector);
                }

                if (!rowElement) {
                    console.warn(`⚠️ No se encontró fila: ${rowConfig.selector}`);
                    continue;
                }

                console.log(`✅ Fila encontrada: ${rowConfig.title}`);

                // ✅ VERIFICAR DIMENSIONES
                const rect = rowElement.getBoundingClientRect();
                if (rect.width <= 0 || rect.height <= 0) {
                    console.warn(`⚠️ Fila sin dimensiones válidas: ${rect.width}x${rect.height}`);
                    continue;
                }

                try {
                    // ✅ ESPERA ESPECÍFICA PARA GOOGLE MAPS EN LA PRIMERA FILA
                    if (i === 0 || rowConfig.identifier === 'recorridos-fila-1') {
                        console.log(`🗺️ Primera fila detectada, esperando Google Maps...`);
                        await this.waitForGoogleMapsToLoad(rowElement);
                    }

                    // ✅ ESPERA GENERAL
                    console.log(`⏳ Espera mínima para: ${rowConfig.title}`);
                    await new Promise(resolve => setTimeout(resolve, 2000));

                    // ✅ CAPTURAR
                    const canvas = await this.captureManager.captureElement(rowElement, rowConfig.identifier);

                    if (canvas && canvas.width > 0 && canvas.height > 0) {
                        sections.push({
                            canvas,
                            title: rowConfig.title,
                            description: `Fila completa - ${rowConfig.title}`,
                            order: i + 1,
                            type: 'row',
                            identifier: rowConfig.identifier
                        });
                        console.log(`✅ Fila capturada exitosamente: ${rowConfig.title} (${canvas.width}x${canvas.height})`);
                    } else {
                        console.error(`❌ Canvas inválido para fila: ${rowConfig.title}`);
                    }

                } catch (error) {
                    console.error(`❌ Error capturando fila ${rowConfig.title}:`, error);
                }
            }

            return sections.sort((a, b) => a.order - b.order);

        } catch (error) {
            console.error('❌ Error en captura de filas configuradas:', error);
            return [];
        }
    }




    /**
     * NUEVO: Obtener selectores alternativos para filas de recorridos
     */
    getAlternativeSelectorsForRow(index, rowConfig) {
        const reporteContainer = '#reporte-content[data-report-area="recorridos"]';

        switch (index) {
            case 0: // Fila 1: Mapa + Velocidad
                return [
                    `${reporteContainer} .row.g-3:first-child`,
                    `${reporteContainer} .row.g-3:not(.mt-3)`,
                    `${reporteContainer} .row:first-child`,
                    // ✅ BUSCAR POR CONTENIDO ESPECÍFICO
                    ...this.getSelectorsByContent(['#mapa-recorridos', '#grafica-velocidad'])
                ].filter(Boolean);

            case 1: // Fila 2: Kilometraje + Visitas
                return [
                    `${reporteContainer} .row.g-3.mt-3`,
                    `${reporteContainer} .row:has(#grafica-kilometraje)`,  // Si soporta :has()
                    `${reporteContainer} .row:nth-child(2)`,
                    // ✅ BUSCAR POR CONTENIDO ESPECÍFICO
                    ...this.getSelectorsByContent(['#grafica-kilometraje', '#visitas-frecuentes'])
                ].filter(Boolean);

            case 2: // Fila 3: Tabla
                return [
                    `${reporteContainer} .row.mt-3:not(.g-3)`,
                    `${reporteContainer} .row:has(#tabla-recorridos)`,  // Si soporta :has()
                    `${reporteContainer} .row:last-child`,
                    // ✅ BUSCAR POR CONTENIDO ESPECÍFICO
                    ...this.getSelectorsByContent(['#tabla-recorridos'])
                ].filter(Boolean);

            default:
                return [];
        }
    }

    getSelectorsByContent(elementIds) {
        const selectors = [];

        for (const elementId of elementIds) {
            const element = document.querySelector(elementId);
            if (element) {
                const parentRow = element.closest('.row');
                if (parentRow) {
                    // Crear selector único basado en clases del row
                    const classes = Array.from(parentRow.classList).join('.');
                    if (classes) {
                        selectors.push(`#reporte-content[data-report-area="recorridos"] .row.${classes}`);
                    }
                }
            }
        }

        return selectors;
    }

    /**
     * NUEVO: Capturar targets configurados manualmente
     */
    async captureConfiguredTargets(captureTargets, sections) {
        try {
            console.log(`📋 Capturando ${captureTargets.length} targets configurados`);

            for (let i = 0; i < captureTargets.length; i++) {
                const target = captureTargets[i];
                console.log(`📸 Capturando: ${target.title}`);

                const element = document.querySelector(target.selector);

                if (!element) {
                    console.warn(`⚠️ No se encontró elemento: ${target.selector}`);
                    continue;
                }

                const targetElement = element.closest('.card') || element;

                if (!this.isElementVisible(targetElement)) {
                    console.warn(`⚠️ Elemento no visible: ${target.title}`);
                    continue;
                }

                try {
                    // ✅ TIEMPO DE ESPERA ESPECÍFICO PARA MAPAS
                    if (target.title.toLowerCase().includes('mapa')) {
                        console.log(`🗺️ Detectado mapa, aplicando espera especial`);
                        await this.waitForMapToLoad(targetElement);
                        await new Promise(resolve => setTimeout(resolve, 2000));
                    } else {
                        await this.waitForChartsToLoad(targetElement);
                        await new Promise(resolve => setTimeout(resolve, 800));
                    }

                    await this.captureManager.prepareElementForCapture(targetElement);
                    const canvas = await this.captureManager.captureElement(targetElement, `target-${i}`);

                    if (canvas && canvas.width > 0 && canvas.height > 0) {
                        sections.push({
                            canvas,
                            title: target.title,
                            description: target.title,
                            order: i + 1,
                            type: target.type,
                            identifier: `target-${i}`
                        });
                        console.log(`✅ Target capturado: ${target.title} (${canvas.width}x${canvas.height})`);
                    }

                    await this.captureManager.restoreElementAfterCapture(targetElement);

                } catch (error) {
                    console.error(`❌ Error capturando ${target.title}:`, error);
                    await this.captureManager.restoreElementAfterCapture(targetElement);
                }
            }

            console.log(`✅ Captura de targets configurados completada: ${sections.length} secciones`);
            return sections;

        } catch (error) {
            console.error('❌ Error en captura de targets configurados:', error);
            return [];
        }
    }

    /**
     * NUEVO: Captura horizontal para recorridos (por filas)
     */
    async captureRecorridosHorizontal() {
        const sections = [];

        try {
            console.log('🖼️ Estrategia HORIZONTAL para recorridos - por filas');

            // ✅ BUSCAR CONTENEDOR DE RECORRIDOS
            const reporteContainer = document.querySelector('#reporte-content[data-report-area="recorridos"]');
            if (!reporteContainer) {
                console.warn('❌ No se encontró contenedor de recorridos');
                return await this.captureRecorridosVertical(); // Fallback
            }

            // ✅ ESPERA INICIAL PARA QUE TODO SE RENDERICE
            console.log('⏳ Espera inicial para renderizado completo...');
            await this.delay(2000);

            // ✅ FILA 1: Mapa + Velocidad (2 elementos de 50% cada uno)
            const fila1 = reporteContainer.querySelector('.row.g-3:first-of-type');
            if (fila1 && this.isElementVisible(fila1)) {
                console.log('📸 Capturando FILA 1: Mapa + Velocidad');
                await this.captureFilaCompletaRecorridos(fila1, 'recorridos-fila-1', 'Mapa y Velocidad', sections);
            } else {
                console.warn('❌ No se encontró FILA 1 (Mapa + Velocidad)');
            }

            // ✅ FILA 2: Kilometraje + Visitas (2 elementos de 50% cada uno)
            const fila2 = reporteContainer.querySelector('.row.g-3.mt-3:first-of-type');
            if (fila2 && this.isElementVisible(fila2)) {
                console.log('📸 Capturando FILA 2: Kilometraje + Visitas');
                await this.captureFilaCompletaRecorridos(fila2, 'recorridos-fila-2', 'Kilometraje y Visitas Frecuentes', sections);
            } else {
                console.warn('❌ No se encontró FILA 2 (Kilometraje + Visitas)');
            }

            // ✅ FILA 3: Tabla (elemento completo de 100%)
            const fila3 = reporteContainer.querySelector('.row.mt-3:last-of-type');
            if (fila3 && this.isElementVisible(fila3)) {
                console.log('📸 Capturando FILA 3: Tabla completa');
                await this.captureFilaCompletaRecorridos(fila3, 'recorridos-fila-3', 'Listado de Recorridos', sections);
            } else {
                console.warn('❌ No se encontró FILA 3 (Tabla)');
            }

            if (sections.length === 0) {
                console.warn('❌ No se capturó ninguna fila, usando captura vertical como fallback');
                return await this.captureRecorridosVertical();
            }

            console.log(`✅ Captura horizontal completada: ${sections.length} filas`);
            return sections;

        } catch (error) {
            console.error('❌ Error en captura horizontal de recorridos:', error);
            return await this.captureRecorridosVertical(); // Fallback
        }
    }

    debugComponentsState(container) {
        console.log('🔍 === DEBUG DE COMPONENTES ===');

        if (!container) {
            console.log('❌ Container no encontrado');
            return;
        }

        // Verificar filas
        const rows = container.querySelectorAll('.row');
        console.log(`📊 Filas encontradas: ${rows.length}`);

        rows.forEach((row, index) => {
            const rect = row.getBoundingClientRect();
            console.log(`   Fila ${index + 1}: ${rect.width.toFixed(0)}x${rect.height.toFixed(0)}`);

            // Verificar mapas en esta fila
            const maps = row.querySelectorAll('[id*="map"], .google-map, .leaflet-container');
            maps.forEach((map, mapIndex) => {
                const mapRect = map.getBoundingClientRect();
                console.log(`     Mapa ${mapIndex + 1}: ${mapRect.width.toFixed(0)}x${mapRect.height.toFixed(0)}`);
            });

            // Verificar canvas en esta fila
            const canvases = row.querySelectorAll('canvas');
            canvases.forEach((canvas, canvasIndex) => {
                console.log(`     Canvas ${canvasIndex + 1}: ${canvas.width}x${canvas.height} (${canvas.getBoundingClientRect().width.toFixed(0)}x${canvas.getBoundingClientRect().height.toFixed(0)})`);
            });
        });
    }

    /**
     * NUEVO: Captura vertical para recorridos (individual)
     */
    async captureRecorridosVertical() {
        const sections = [];
        const targets = this.options.captureTargets;

        console.log('📱 Estrategia VERTICAL para recorridos - individual');
        console.log(`📋 Capturando ${targets.length} targets especificados`);

        for (let i = 0; i < targets.length; i++) {
            const target = targets[i];
            console.log(`📸 Capturando: ${target.title}`);

            const element = document.querySelector(target.selector);

            if (!element) {
                console.warn(`⚠️ No se encontró elemento: ${target.selector}`);
                continue;
            }

            const targetElement = element.closest('.card') || element;

            if (!this.isElementVisible(targetElement)) {
                console.warn(`⚠️ Elemento no visible: ${target.title}`);
                continue;
            }

            try {
                // ✅ TIEMPO DE ESPERA ESPECÍFICO PARA MAPAS
                if (target.title.toLowerCase().includes('mapa')) {
                    console.log(`🗺️ Detectado mapa, aplicando espera especial`);
                    await this.waitForMapToLoad(targetElement);
                    await new Promise(resolve => setTimeout(resolve, 2000));
                } else {
                    await this.waitForChartsToLoad(targetElement);
                    await new Promise(resolve => setTimeout(resolve, 800));
                }

                await this.captureManager.prepareElementForCapture(targetElement);
                const canvas = await this.captureManager.captureElement(targetElement, `recorridos-target-${i}`);

                if (canvas && canvas.width > 0 && canvas.height > 0) {
                    sections.push({
                        canvas,
                        title: target.title,
                        description: target.title,
                        order: i + 1,
                        type: target.type,
                        identifier: `recorridos-target-${i}`
                    });
                    console.log(`✅ Target capturado: ${target.title} (${canvas.width}x${canvas.height})`);
                }

                await this.captureManager.restoreElementAfterCapture(targetElement);

            } catch (error) {
                console.error(`❌ Error capturando ${target.title}:`, error);
                await this.captureManager.restoreElementAfterCapture(targetElement);
            }
        }

        console.log(`✅ Captura vertical de recorridos completada: ${sections.length} secciones`);
        return sections;
    }



    /**
     * NUEVO: Esperar a que todos los componentes de una fila estén listos
     */
    async waitForRowComponentsToLoad(filaElement) {
        console.log('⏳ Esperando a que todos los componentes de la fila estén listos...');

        // ✅ BUSCAR TODOS LOS COMPONENTES EN LA FILA
        const componentContainers = filaElement.querySelectorAll('[id^="mapa-"], [id^="grafica-"], [id^="visitas-"], [id^="tabla-"]');

        console.log(`🔍 Encontrados ${componentContainers.length} componentes en la fila`);

        for (const container of componentContainers) {
            const componentId = container.id;
            console.log(`⏳ Esperando componente: ${componentId}`);

            // ✅ VERIFICAR QUE EL CONTENEDOR TENGA DIMENSIONES VÁLIDAS
            await this.waitForElementToHaveDimensions(container, componentId);

            // ✅ ESPERA ESPECÍFICA POR TIPO DE COMPONENTE
            if (componentId.includes('mapa')) {
                await this.waitForMapToLoad(container);
                await this.waitForSmartComponentToComplete(container);
                await new Promise(resolve => setTimeout(resolve, 2000)); // Más tiempo para mapas
            } else if (componentId.includes('grafica') || componentId.includes('visitas')) {
                await this.waitForChartsToLoad(container);
                await this.waitForSmartComponentToComplete(container);
                await new Promise(resolve => setTimeout(resolve, 1000));
            } else if (componentId.includes('tabla')) {
                await this.waitForTableToLoad(container);
                await this.waitForSmartComponentToComplete(container);
                await new Promise(resolve => setTimeout(resolve, 500));
            }

            console.log(`✅ Componente listo: ${componentId}`);
        }

        // ✅ ESPERA ADICIONAL PARA ASEGURAR RENDERIZADO COMPLETO
        console.log('⏳ Espera final para renderizado completo...');
        await new Promise(resolve => setTimeout(resolve, 1500));

        console.log('✅ Todos los componentes de la fila están listos');
    }

    /**
     * NUEVO: Esperar a que un elemento tenga dimensiones válidas
     */
    async waitForElementToHaveDimensions(element, elementId) {
        console.log(`📏 Verificando dimensiones de: ${elementId}`);

        let attempts = 0;
        const maxAttempts = 20;

        while (attempts < maxAttempts) {
            const rect = element.getBoundingClientRect();
            const hasValidDimensions = rect.width > 0 && rect.height > 0;

            console.log(`📏 ${elementId} - Intento ${attempts + 1}: ${rect.width}x${rect.height}`);

            if (hasValidDimensions) {
                console.log(`✅ ${elementId} tiene dimensiones válidas: ${rect.width}x${rect.height}`);
                return true;
            }

            await new Promise(resolve => setTimeout(resolve, 500));
            attempts++;
        }

        console.warn(`⚠️ ${elementId} no alcanzó dimensiones válidas después de ${maxAttempts} intentos`);
        return false;
    }

    /**
     * NUEVO: Esperar a que SmartComponentRenderer complete
     */
    async waitForSmartComponentToComplete(container) {
        console.log('⏳ Esperando a que SmartComponentRenderer complete...');

        let attempts = 0;
        const maxAttempts = 15;

        while (attempts < maxAttempts) {
            // ✅ VERIFICAR QUE NO HAY SPINNERS ACTIVOS
            const spinners = container.querySelectorAll('.spinner-border, .spinner-grow');
            const skeletons = container.querySelectorAll('[class*="skeleton"], [class*="loading"]');
            const loadingText = container.querySelector('[class*="loading"], .text-muted');

            const isComplete = spinners.length === 0 && skeletons.length === 0;

            if (isComplete) {
                // ✅ VERIFICAR QUE HAY CONTENIDO REAL
                const hasRealContent = container.querySelector('canvas, svg, img, .chart, .map, table');

                if (hasRealContent) {
                    console.log('✅ SmartComponentRenderer completado con contenido real');
                    return true;
                }
            }

            console.log(`🔄 SmartComponentRenderer esperando (intento ${attempts + 1})`);
            await new Promise(resolve => setTimeout(resolve, 500));
            attempts++;
        }

        console.warn('⚠️ SmartComponentRenderer no completó en el tiempo esperado');
        return false;
    }

    /**
     * NUEVO: Esperar a que las tablas estén cargadas
     */
    async waitForTableToLoad(element) {
        console.log('📊 Esperando a que la tabla esté completamente cargada...');

        let attempts = 0;
        while (attempts < 10) {
            const table = element.querySelector('table');
            const rows = element.querySelectorAll('tbody tr');

            if (table && rows.length > 0) {
                console.log(`✅ Tabla encontrada con ${rows.length} filas`);
                break;
            }

            console.log(`🔄 Esperando tabla (intento ${attempts + 1})`);
            await new Promise(resolve => setTimeout(resolve, 500));
            attempts++;
        }
    }

    async captureSpecifiedTargets() {
        const sections = [];
        const targets = this.options.captureTargets;

        console.log(`📋 Capturando ${targets.length} targets especificados`);

        for (let i = 0; i < targets.length; i++) {
            const target = targets[i];
            console.log(`📸 Capturando: ${target.title}`);

            // ✅ BUSCAR ELEMENTO POR SELECTOR
            const element = document.querySelector(target.selector);

            if (!element) {
                console.warn(`⚠️ No se encontró elemento: ${target.selector}`);
                continue;
            }

            // ✅ BUSCAR LA CARD PADRE O USAR EL ELEMENTO DIRECTO
            const targetElement = element.closest('.card') || element;

            if (!this.isElementVisible(targetElement)) {
                console.warn(`⚠️ Elemento no visible: ${target.title}`);
                continue;
            }

            try {
                console.log(`🔍 Iniciando captura de: ${target.title}`);

                // ✅ TIEMPO DE ESPERA ESPECÍFICO PARA MAPAS
                if (target.title.toLowerCase().includes('mapa') ||
                    target.selector.includes('mapa')) {
                    console.log(`🗺️ Detectado componente de mapa, aplicando espera especial`);
                    await this.waitForMapToLoad(targetElement);
                    await new Promise(resolve => setTimeout(resolve, 2000)); // 2 segundos adicionales
                } else {
                    // ✅ ESPERA ESTÁNDAR PARA OTROS COMPONENTES
                    await this.waitForChartsToLoad(targetElement);
                    await new Promise(resolve => setTimeout(resolve, 800));
                }

                // ✅ PREPARAR Y CAPTURAR
                await this.captureManager.prepareElementForCapture(targetElement);
                const canvas = await this.captureManager.captureElement(targetElement, `target-${i}`);

                if (canvas && canvas.width > 0 && canvas.height > 0) {
                    sections.push({
                        canvas,
                        title: target.title,
                        description: `${target.type} - ${target.title}`,
                        order: i + 1,
                        type: target.type,
                        identifier: `target-${i}`
                    });
                    console.log(`✅ Target capturado exitosamente: ${target.title} (${canvas.width}x${canvas.height})`);
                } else {
                    console.warn(`⚠️ Canvas inválido para: ${target.title} (${canvas?.width || 0}x${canvas?.height || 0})`);
                }

                await this.captureManager.restoreElementAfterCapture(targetElement);

            } catch (error) {
                console.error(`❌ Error capturando ${target.title}:`, error);

                // ✅ INTENTAR CAPTURA DE EMERGENCIA PARA MAPAS
                if (target.title.toLowerCase().includes('mapa')) {
                    console.log(`🔄 Intentando captura de emergencia para mapa`);
                    try {
                        const emergencyCanvas = await this.captureMapFallback(targetElement, target.title);
                        if (emergencyCanvas) {
                            sections.push({
                                canvas: emergencyCanvas,
                                title: target.title + ' (Captura de Emergencia)',
                                description: `${target.type} - ${target.title} (Emergencia)`,
                                order: i + 1,
                                type: target.type,
                                identifier: `target-${i}-emergency`
                            });
                            console.log(`✅ Captura de emergencia exitosa para: ${target.title}`);
                        }
                    } catch (emergencyError) {
                        console.error(`❌ Captura de emergencia también falló:`, emergencyError);
                    }
                }

                await this.captureManager.restoreElementAfterCapture(targetElement);
            }
        }

        console.log(`✅ Captura de targets completada: ${sections.length} secciones`);
        return sections;
    }

    async waitForMapToLoad(element) {
        console.log(`🗺️ Esperando a que el mapa esté completamente cargado...`);

        // ✅ ESPERAR A GOOGLE MAPS PROVIDER
        let attempts = 0;
        while (attempts < 15) { // Más intentos para mapas
            const googleMapsContainer = element.querySelector('.gm-style, [class*="google-map"], .leaflet-container');
            if (googleMapsContainer) {
                console.log(`✅ Contenedor de Google Maps encontrado en intento ${attempts + 1}`);
                break;
            }
            await new Promise(resolve => setTimeout(resolve, 500));
            attempts++;
        }

        // ✅ ESPERAR A QUE LAS TILES ESTÉN CARGADAS
        attempts = 0;
        while (attempts < 10) {
            const mapImages = element.querySelectorAll('img[src*="maps.googleapis.com"], img[src*="google.com/maps"]');
            const loadedImages = Array.from(mapImages).filter(img => img.complete && img.naturalHeight > 0);

            if (mapImages.length > 0 && loadedImages.length === mapImages.length) {
                console.log(`✅ Todas las imágenes del mapa están cargadas (${loadedImages.length}/${mapImages.length})`);
                break;
            }

            console.log(`🔄 Esperando imágenes del mapa: ${loadedImages.length}/${mapImages.length} cargadas`);
            await new Promise(resolve => setTimeout(resolve, 800));
            attempts++;
        }

        // ✅ ESPERAR A ELEMENTOS ESPECÍFICOS DE GOOGLE MAPS
        attempts = 0;
        while (attempts < 8) {
            const gmStyle = element.querySelector('.gm-style');
            const gmCanvas = element.querySelector('.gm-style canvas');

            if (gmStyle && gmCanvas) {
                console.log(`✅ Google Maps completamente renderizado`);
                break;
            }

            console.log(`🔄 Esperando renderizado completo de Google Maps (intento ${attempts + 1})`);
            await new Promise(resolve => setTimeout(resolve, 1000));
            attempts++;
        }

        console.log(`✅ Espera de mapa completada`);
    }

    /**
     * NUEVO: Captura de emergencia para mapas problemáticos
     */
    async captureMapFallback(element, title) {
        try {
            console.log(`🚨 Iniciando captura de emergencia para: ${title}`);

            // ✅ INTENTAR CAPTURA CON CONFIGURACIÓN ESPECIAL PARA MAPAS
            const canvas = await html2canvas(element, {
                backgroundColor: '#ffffff',
                scale: 0.8, // Menor escala para mapas
                useCORS: true,
                allowTaint: false,
                logging: false,
                timeout: 10000, // Timeout más largo
                onclone: (clonedDoc) => {
                    // ✅ MODIFICAR EL DOM CLONADO PARA MEJORAR CAPTURA
                    const mapContainers = clonedDoc.querySelectorAll('.gm-style, [class*="google-map"]');
                    mapContainers.forEach(container => {
                        container.style.transform = 'none';
                        container.style.transition = 'none';
                    });
                },
                ignoreElements: (element) => {
                    // ✅ IGNORAR ELEMENTOS PROBLEMÁTICOS
                    return element.classList.contains('gm-control-active') ||
                        element.classList.contains('gm-svpc') ||
                        element.tagName === 'IFRAME';
                }
            });

            if (canvas && canvas.width > 50 && canvas.height > 50) {
                console.log(`✅ Captura de emergencia exitosa: ${canvas.width}x${canvas.height}`);
                return canvas;
            }

            console.warn(`⚠️ Captura de emergencia produjo canvas muy pequeño: ${canvas?.width || 0}x${canvas?.height || 0}`);
            return null;

        } catch (error) {
            console.error(`❌ Error en captura de emergencia:`, error);
            return null;
        }
    }


    async captureAlertasSpecific(isHorizontalPrint, sections) {
        if (isHorizontalPrint) {
            // ✅ HORIZONTAL: Tu código original de alertas
            const fila1 = document.querySelector('.g-3 .row.g-3.mb-3:first-of-type');
            if (fila1 && this.isElementVisible(fila1)) {
                console.log('📸 Capturando FILA 1 COMPLETA: Vehículos + Alertas');
                await this.captureFilaCompleta(fila1, 'alertas-fila-1', sections);
            }

            const fila2 = document.querySelector('.g-3 .row.g-3.mb-3:nth-of-type(2)');
            if (fila2 && this.isElementVisible(fila2)) {
                console.log('📸 Capturando FILA 2 COMPLETA: Mapa + Frecuencia');
                await this.captureFilaCompleta(fila2, 'alertas-fila-2', sections);
            }
        } else {
            // ✅ VERTICAL: Tu código original de alertas
            console.log('📊 Estrategia VERTICAL para alertas');

            const componentes = [
                { selector: '.g-3 .row.g-3.mb-3:first-of-type .col-md-6:first-child', name: 'Análisis de Vehículos' },
                { selector: '.g-3 .row.g-3.mb-3:first-of-type .col-md-6:last-child', name: 'Análisis de Alertas' },
                { selector: '.g-3 .row.g-3.mb-3:nth-of-type(2) .col-md-6:first-child', name: 'Mapa de Alertas' },
                { selector: '.g-3 .row.g-3.mb-3:nth-of-type(2) .col-md-6:last-child', name: 'Análisis de Frecuencia' }
            ];

            for (const comp of componentes) {
                const element = document.querySelector(comp.selector);
                if (element && this.isElementVisible(element)) {
                    console.log(`📸 Capturando: ${comp.name}`);
                    await this.captureUniversalComponent_Simple(element, comp.name, sections);
                }
            }
        }

        return sections.sort((a, b) => a.order - b.order);
    }


    async captureUniversalAuto(sections) {
        try {
            // 🎯 ANÁLISIS AUTOMÁTICO DE LA ESTRUCTURA
            const structure = this.captureManager.analyzeReportStructure();
            const printConfig = this.getCurrentPrintConfiguration();
            const isHorizontalPrint = printConfig.orientation === 'landscape';

            console.log(`📊 Estructura detectada:`, structure);
            console.log(`📏 Orientación: ${isHorizontalPrint ? 'HORIZONTAL' : 'VERTICAL'}`);

            // 🎯 DECIDIR ESTRATEGIA
            if (structure.reportType === 'alertas') {
                // ✅ MANTENER lógica específica de alertas
                console.log('🚨 Usando estrategia ESPECÍFICA para ALERTAS');
                return await this.captureAlertasSpecific(isHorizontalPrint, sections);
            } else {
                // ✅ USAR sistema universal para otros reportes
                console.log(`🔄 Usando estrategia UNIVERSAL para ${structure.reportType.toUpperCase()}`);
                return await this.captureUniversalAutoGeneric(structure, isHorizontalPrint, sections);
            }

        } catch (error) {
            console.error('❌ Error en captura universal:', error);
            return [];
        }
    }

    async captureUniversalAutoGeneric(structure, isHorizontalPrint, sections) {
        if (isHorizontalPrint) {
            // 🔄 HORIZONTAL: Capturar por filas completas
            console.log('📊 Estrategia HORIZONTAL genérica - por filas');

            const visualRows = structure.layout.rowPatterns
                .filter(row => row.contentType === 'visual' || row.contentType === 'mixed')
                .slice(0, 3); // Máximo 3 filas

            for (const rowPattern of visualRows) {
                const allRows = document.querySelectorAll('.row');
                const rowElement = allRows[rowPattern.rowIndex - 1];

                if (rowElement && this.isElementVisible(rowElement)) {
                    console.log(`📸 Capturando fila ${rowPattern.rowIndex}`);
                    await this.captureFilaCompleta(
                        rowElement,
                        `generic-fila-${rowPattern.rowIndex}`,
                        sections
                    );
                }
            }

        } else {
            // 📱 VERTICAL: Capturar componentes individuales
            console.log('📊 Estrategia VERTICAL genérica - por componentes');

            const componentesValidos = structure.components
                .filter(comp => comp.type !== 'table')
                .slice(0, 4); // Máximo 4 componentes

            for (const component of componentesValidos) {
                console.log(`📸 Capturando ${component.type}`);
                await this.captureUniversalComponent_Simple(
                    component.element,
                    component.title,
                    sections
                );
            }
        }

        return sections.sort((a, b) => a.order - b.order);
    }

    /**
     * NUEVO: Captura simple para componentes individuales
     */
    async captureUniversalComponent_Simple(element, title, sections) {
        try {
            await this.captureManager.prepareElementForCapture(element);
            const canvas = await this.captureManager.captureElement(element, `comp-${sections.length}`);

            if (canvas && canvas.width > 0 && canvas.height > 0) {
                sections.push({
                    canvas,
                    title: title || 'Componente',
                    description: title || 'Componente del reporte',
                    order: sections.length + 1,
                    type: 'component',
                    identifier: `comp-${sections.length}`
                });
                console.log(`✅ Componente capturado: ${title}`);
            }

            await this.captureManager.restoreElementAfterCapture(element);

        } catch (error) {
            console.error(`❌ Error capturando componente ${title}:`, error);
            await this.captureManager.restoreElementAfterCapture(element);
        }
    }

    async captureUniversalAuto_rs(sections) {
        // 🎯 ANÁLISIS AUTOMÁTICO DE LA ESTRUCTURA
        const structure = this.captureManager.analyzeReportStructure();
        const printConfig = this.getCurrentPrintConfiguration();
        const isHorizontalPrint = printConfig.orientation === 'landscape';

        console.log(`📏 Orientación: ${isHorizontalPrint ? 'HORIZONTAL' : 'VERTICAL'}`);

        if (isHorizontalPrint) {
            // 🔄 HORIZONTAL: Capturar por filas completas
            const visualRows = structure.layout.rowPatterns.filter(row =>
                row.contentType === 'visual' || row.contentType === 'mixed'
            );

            for (const rowPattern of visualRows) {
                const rowElement = document.querySelector(`.row:nth-of-type(${rowPattern.rowIndex})`);

                if (rowElement && this.isElementVisible(rowElement)) {
                    console.log(`📸 Capturando fila ${rowPattern.rowIndex} (${rowPattern.contentType})`);
                    await this.captureFilaCompleta(
                        rowElement,
                        `universal-fila-${rowPattern.rowIndex}`,
                        sections
                    );
                }
            }
        } else {
            // 📱 VERTICAL: Capturar componentes individuales
            for (const component of structure.components) {
                if (component.type !== 'table') {
                    console.log(`📸 Capturando ${component.type} (componente ${component.index})`);

                    await this.captureUniversalComponent(
                        component,
                        `universal-${component.type}-${component.index}`,
                        sections
                    );
                }
            }
        }

        return sections.sort((a, b) => a.order - b.order);
    }

    // ✅ NUEVO: Capturar componente universal
    async captureUniversalComponent_respadldo_08062025_02_(component, identifier, sections) {
        try {
            const element = component.element;

            await this.captureManager.prepareElementForCapture(element);
            const canvas = await this.captureManager.captureElement(element, identifier);

            if (canvas) {
                sections.push({
                    canvas,
                    title: this.generateUniversalTitle(component),
                    description: `${component.type} - Componente ${component.index}`,
                    order: sections.length + 1,
                    type: component.type,
                    identifier
                });
            }

            await this.captureManager.restoreElementAfterCapture(element);

        } catch (error) {
            console.error(`❌ Error capturando componente ${identifier}:`, error);
        }
    }

    async captureUniversalComponent(component, identifier, sections) {
        try {
            const element = component.element;

            console.log(`📸 Iniciando captura: ${identifier}`);
            console.log(`🔍 Elemento:`, element.tagName, element.className);

            // ✅ VALIDAR ELEMENTO ANTES DE CAPTURAR
            if (!this.validateElementForCapture(element)) {
                console.warn(`⚠️ Elemento no válido para captura: ${identifier}`);
                return;
            }

            // ✅ ESPERAR A QUE GRÁFICOS ESTÉN LISTOS
            await this.waitForChartsToLoad(element);

            // ✅ PREPARAR ELEMENTO
            await this.captureManager.prepareElementForCapture(element);

            // ✅ ESPERA ADICIONAL PARA ESTABILIZACIÓN
            await new Promise(resolve => setTimeout(resolve, 800));

            // ✅ CAPTURAR CON VALIDACIÓN
            const canvas = await this.captureManager.captureElement(element, identifier);

            if (canvas && canvas.width > 0 && canvas.height > 0) {
                sections.push({
                    canvas,
                    title: component.title || this.generateUniversalTitle(component),
                    description: `${component.type} - Componente ${component.index}`,
                    order: sections.length + 1,
                    type: component.type,
                    identifier
                });
                console.log(`✅ Componente capturado exitosamente: ${identifier}`);
            } else {
                console.warn(`⚠️ Canvas inválido para: ${identifier}`);
            }

            await this.captureManager.restoreElementAfterCapture(element);

        } catch (error) {
            console.error(`❌ Error capturando componente ${identifier}:`, error);

            // ✅ FALLBACK: Intentar captura simple
            try {
                console.log(`🔄 Intentando captura fallback para: ${identifier}`);
                await this.captureSimpleFallback(component, identifier, sections);
            } catch (fallbackError) {
                console.error(`❌ Fallback también falló para ${identifier}:`, fallbackError);
            }
        }
    }

    /**
     * NUEVO: Validar elemento antes de captura
     */
    validateElementForCapture(element) {
        if (!element || !element.getBoundingClientRect) {
            console.warn('❌ Elemento no válido o no tiene getBoundingClientRect');
            return false;
        }

        const rect = element.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) {
            console.warn('❌ Elemento tiene dimensiones 0:', rect);
            return false;
        }

        const computedStyle = window.getComputedStyle(element);
        if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden') {
            console.warn('❌ Elemento no visible');
            return false;
        }

        return true;
    }

    /**
     * NUEVO: Esperar a que los gráficos estén completamente cargados
     */
    async waitForChartsToLoad(element) {
        // ✅ ESPERAR ECHARTS
        const echartsContainers = element.querySelectorAll('.echarts-container, [class*="echarts"]');
        for (const container of echartsContainers) {
            let attempts = 0;
            while (attempts < 10) {
                const canvas = container.querySelector('canvas');
                if (canvas && canvas.width > 0 && canvas.height > 0) {
                    console.log('✅ ECharts canvas listo');
                    break;
                }
                await new Promise(resolve => setTimeout(resolve, 200));
                attempts++;
            }
        }

        // ✅ ESPERAR MAPAS (Leaflet/Google)
        const mapContainers = element.querySelectorAll('.leaflet-container, [class*="google-map"]');
        for (const container of mapContainers) {
            await new Promise(resolve => setTimeout(resolve, 1000)); // Maps need more time
        }

        // ✅ ESPERAR CANVAS GENERALES
        const canvasElements = element.querySelectorAll('canvas');
        for (const canvas of canvasElements) {
            let attempts = 0;
            while (attempts < 10 && (canvas.width === 0 || canvas.height === 0)) {
                await new Promise(resolve => setTimeout(resolve, 200));
                attempts++;
            }
        }

        console.log(`✅ Gráficos estabilizados en elemento`);
    }

    /**
     * NUEVO: Captura simple como fallback
     */
    async captureSimpleFallback(component, identifier, sections) {
        const element = component.element;

        // ✅ CAPTURA DIRECTA CON html2canvas básico
        const canvas = await html2canvas(element, {
            backgroundColor: '#ffffff',
            scale: 1,
            useCORS: true,
            allowTaint: true,
            logging: false,
            ignoreElements: (element) => {
                // Ignorar elementos problemáticos
                return element.classList.contains('animated') ||
                    element.classList.contains('loading');
            }
        });

        if (canvas && canvas.width > 0 && canvas.height > 0) {
            sections.push({
                canvas,
                title: component.title || `Componente ${component.index}`,
                description: `${component.type} - Captura básica`,
                order: sections.length + 1,
                type: component.type,
                identifier: `${identifier}-fallback`
            });
            console.log(`✅ Captura fallback exitosa: ${identifier}`);
        }
    }



    generateUniversalTitle(component) {
        const titleMap = {
            'chart': 'Gráfico',
            'vehicle-chart': 'Análisis de Vehículos',
            'alert-chart': 'Análisis de Alertas',
            'speed-chart': 'Análisis de Velocidad',
            'distance-chart': 'Análisis de Distancia',
            'map': 'Mapa de Ubicaciones',
            'table': 'Tabla de Datos',
            'generic-visual': 'Componente Visual'
        };

        return titleMap[component.type] || 'Análisis de Datos';
    }


    // ✅ MANTENER: Lógica específica de captura de secciones (NO duplicada)
    async captureUniversalSections_backparagenericos() {
        const sections = [];

        console.log('📋 🚨 Iniciando captura especializada para REPORTE DE ALERTAS...');

        try {
            // 🎯 DETECTAR ORIENTACIÓN DE IMPRESIÓN
            const printConfig = this.getCurrentPrintConfiguration();
            const isHorizontalPrint = printConfig.orientation === 'landscape';

            console.log(`📏 Orientación detectada: ${isHorizontalPrint ? 'HORIZONTAL' : 'VERTICAL'}`);

            if (isHorizontalPrint) {
                // 🔄 ESTRATEGIA HORIZONTAL: Capturar filas completas
                console.log('📊 Aplicando estrategia HORIZONTAL - captura por filas completas');

                // 🔍 FILA 1: Vehículos + Alertas Detonadas (FILA COMPLETA)
                const fila1 = document.querySelector('.g-3 .row.g-3.mb-3:first-of-type');
                if (fila1 && this.isElementVisible(fila1)) {
                    console.log('📸 Capturando FILA 1 COMPLETA: Vehículos + Alertas');
                    await this.captureFilaCompleta(fila1, 'fila-1-completa', sections);
                }

                // 🔍 FILA 2: Mapa + Frecuencia (FILA COMPLETA)
                const fila2 = document.querySelector('.g-3 .row.g-3.mb-3:nth-of-type(2)');
                if (fila2 && this.isElementVisible(fila2)) {
                    console.log('📸 Capturando FILA 2 COMPLETA: Mapa + Frecuencia');
                    await this.captureFilaCompleta(fila2, 'fila-2-completa', sections);
                }

            } else {
                // 📱 ESTRATEGIA VERTICAL: Tu lógica original que SÍ funciona
                console.log('📊 Aplicando estrategia VERTICAL - captura por columnas individuales');

                // 🔍 FILA 1: Vehículos + Alertas Detonadas (COLUMNAS INDIVIDUALES)
                const fila1 = document.querySelector('.g-3 .row.g-3.mb-3:first-of-type');
                if (fila1 && this.isElementVisible(fila1)) {
                    console.log('📊 Capturando FILA 1: Vehículos + Alertas');

                    const columnas1 = fila1.querySelectorAll('.col-md-6');
                    for (let i = 0; i < columnas1.length; i++) {
                        const col = columnas1[i];
                        if (this.isElementVisible(col)) {
                            await this.captureColumnSection(col, `fila-1-col-${i + 1}`, sections);
                        }
                    }
                }

                // 🔍 FILA 2: Mapa + Frecuencia (COLUMNAS INDIVIDUALES)
                const fila2 = document.querySelector('.g-3 .row.g-3.mb-3:nth-of-type(2)');
                if (fila2 && this.isElementVisible(fila2)) {
                    console.log('🗺️ Capturando FILA 2: Mapa + Frecuencia');

                    const columnas2 = fila2.querySelectorAll('.col-md-6');
                    for (let i = 0; i < columnas2.length; i++) {
                        const col = columnas2[i];
                        if (this.isElementVisible(col)) {
                            await this.captureColumnSection(col, `fila-2-col-${i + 1}`, sections);
                        }
                    }
                }
            }

            console.log(`✅ Captura de alertas completada: ${sections.length} secciones`);
            return sections.sort((a, b) => a.order - b.order);

        } catch (error) {
            console.error('❌ Error en captura de alertas:', error);
            return [];
        }
    }

    /**
     * Nuevo método: Capturar fila completa para horizontal
     */
    async captureFilaCompleta(fila, identifier, sections) {
        try {
            console.log(`📸 Capturando fila completa: ${identifier}`);

            await this.prepareElementForCapture(fila);

            const canvas = await this.captureElement(fila, identifier);

            if (canvas) {
                sections.push({
                    canvas,
                    title: this.getFilaTitleFromContent(fila, identifier),
                    description: 'Fila completa con múltiples componentes',
                    order: sections.length + 1,
                    type: 'fila-completa',
                    identifier
                });
                console.log(`✅ Fila completa capturada: ${identifier}`);
            }

            await this.restoreElementAfterCapture(fila);

        } catch (error) {
            console.error(`❌ Error capturando fila completa ${identifier}:`, error);

            // Fallback: usar captura por columnas individuales
            console.log(`🔄 Fallback: capturando columnas individuales de ${identifier}`);
            const columnas = fila.querySelectorAll('.col-md-6, .col-md-12');
            for (let i = 0; i < columnas.length; i++) {
                const col = columnas[i];
                if (this.isElementVisible(col)) {
                    await this.captureColumnSection(col, `${identifier}-col-${i + 1}`, sections);
                }
            }
        }
    }

    /**
     * Obtener título de fila basado en contenido
     */
    getFilaTitleFromContent(fila, identifier) {
        const text = fila.textContent.toLowerCase();

        if (text.includes('vehículo') && text.includes('alerta')) {
            return 'Vehículos y Alertas Detonadas';
        }
        if (text.includes('mapa') && text.includes('frecuencia')) {
            return 'Mapa de Ubicación y Análisis de Frecuencia';
        }
        if (text.includes('tabla')) {
            return 'Tabla de Datos';
        }

        return `Análisis ${identifier}`;
    }



    async captureUniversalSections_respaldo() {
        const sections = [];

        console.log('📋 🚨 Iniciando captura especializada para REPORTE DE ALERTAS...');

        try {
            // ✅ ESTRATEGIA ESPECÍFICA PARA ALERTAS: Por filas y columnas

            // 🔍 FILA 1: Vehículos + Alertas Detonadas  
            const fila1 = document.querySelector('.g-3 .row.g-3.mb-3:first-of-type');
            if (fila1 && this.isElementVisible(fila1)) {
                console.log('📊 Capturando FILA 1: Vehículos + Alertas');

                const columnas1 = fila1.querySelectorAll('.col-md-6');
                for (let i = 0; i < columnas1.length; i++) {
                    const col = columnas1[i];
                    if (this.isElementVisible(col)) {
                        await this.captureColumnSection(col, `fila-1-col-${i + 1}`, sections);
                    }
                }
            }

            // 🔍 FILA 2: Mapa + Frecuencia
            const fila2 = document.querySelector('.g-3 .row.g-3.mb-3:nth-of-type(2)');
            if (fila2 && this.isElementVisible(fila2)) {
                console.log('🗺️ Capturando FILA 2: Mapa + Frecuencia');

                const columnas2 = fila2.querySelectorAll('.col-md-6');
                for (let i = 0; i < columnas2.length; i++) {
                    const col = columnas2[i];
                    if (this.isElementVisible(col)) {
                        await this.captureColumnSection(col, `fila-2-col-${i + 1}`, sections);
                    }
                }
            }

            console.log(`✅ Captura de alertas completada: ${sections.length} secciones`);
            return sections.sort((a, b) => a.order - b.order);

        } catch (error) {
            console.error('❌ Error en captura de alertas:', error);
            return [];
        }
    }

    // ✅ MANTENER: Métodos específicos de detección de contenido (NO duplicados)
    // async captureColumnSection(column, identifier, sections) {
    //     try {
    //         if (!this.isElementVisible(column)) {
    //             return;
    //         }

    //         const tipoContenido = this.detectContentType(column);

    //         if (tipoContenido.isTable) {
    //             console.log(`📋 Columna ${identifier} es solo tabla - omitiendo de gráficos`);
    //             return;
    //         }

    //         if (!tipoContenido.hasVisualContent) {
    //             console.log(`⚠️ Columna ${identifier} sin contenido visual`);
    //             return;
    //         }

    //         await this.prepareElementForCapture(column);

    //         try {
    //             const canvas = await this.captureElement(column, 'section');
    //             const title = this.getColumnTitle(column, identifier, tipoContenido);

    //             sections.push({
    //                 canvas,
    //                 title,
    //                 description: tipoContenido.description,
    //                 order: sections.length + 1,
    //                 type: 'column',
    //                 identifier
    //             });

    //             console.log(`✅ Columna capturada: ${title}`);

    //         } catch (error) {
    //             console.error(`❌ Error capturando columna ${identifier}:`, error);
    //         } finally {
    //             await this.restoreElementAfterCapture(column);
    //         }

    //     } catch (error) {
    //         console.error(`❌ Error procesando columna ${identifier}:`, error);
    //     }
    // }
    async captureColumnSection(column, identifier, sections) {
        try {
            if (!this.isElementVisible(column)) {
                console.log(`⚠️ Columna ${identifier} no visible, saltando`);
                return;
            }

            const tipoContenido = this.detectContentType(column);

            console.log(`📋 Columna ${identifier}:`, {
                hasVisualContent: tipoContenido.hasVisualContent,
                isTable: tipoContenido.isTable,
                description: tipoContenido.description
            });

            if (tipoContenido.isTable) {
                console.log(`📋 Columna ${identifier} es solo tabla - omitiendo de gráficos`);
                return;
            }

            if (!tipoContenido.hasVisualContent) {
                console.log(`⚠️ Columna ${identifier} sin contenido visual detectado`);
                // ✅ CAPTURAR DE TODAS FORMAS si contiene texto relevante
                const text = column.textContent.toLowerCase();
                if (!text.includes('vehículo') && !text.includes('alerta') && !text.includes('ubicación')) {
                    return;
                }
                console.log(`🔄 Intentando capturar ${identifier} por contenido de texto`);
            }

            await this.prepareElementForCapture(column);

            try {
                console.log(`📸 Capturando elemento ${identifier}...`);
                const canvas = await this.captureElement(column, 'section');

                if (!canvas) {
                    console.error(`❌ No se pudo capturar canvas de ${identifier}`);
                    return;
                }

                const title = this.getColumnTitle(column, identifier, tipoContenido);

                sections.push({
                    canvas,
                    title,
                    description: tipoContenido.description,
                    order: sections.length + 1,
                    type: 'column',
                    identifier
                });

                console.log(`✅ Columna capturada exitosamente: ${title}`);

            } catch (error) {
                console.error(`❌ Error capturando columna ${identifier}:`, error);
            } finally {
                await this.restoreElementAfterCapture(column);
            }

        } catch (error) {
            console.error(`❌ Error procesando columna ${identifier}:`, error);
        }
    }

    detectContentType(column) {
        try {
            console.log(`🔍 Detectando contenido en columna con clases: ${column.className}`);

            // ✅ DETECCIÓN MEJORADA DE CANVAS/SVG
            const hasCanvas = !!(
                column.querySelector('canvas') ||
                column.querySelector('svg') ||
                column.querySelector('[data-recharts]') ||
                column.querySelector('.recharts-wrapper') ||
                column.querySelector('.chart-wrapper') ||
                column.querySelector('[class*="chart"]') ||
                column.querySelector('[id*="chart"]')
            );

            // ✅ DETECCIÓN ESPECÍFICA PARA COMPONENTES DE ALERTAS
            const hasAlertasDetonadas = !!(
                column.querySelector('[data-component*="alertas"]') ||
                column.querySelector('[class*="AlertasDetonadas"]') ||
                column.textContent.toLowerCase().includes('alertas detonadas') ||
                column.textContent.toLowerCase().includes('total de alertas') ||
                (column.querySelector('.card-header') &&
                    column.querySelector('.card-header').textContent.toLowerCase().includes('alertas'))
            );

            const hasAlertaFrecuencia = !!(
                column.querySelector('[data-component*="frecuencia"]') ||
                column.querySelector('[class*="AlertaFrecuencia"]') ||
                column.textContent.toLowerCase().includes('frecuencia') ||
                column.textContent.toLowerCase().includes('comparativo') ||
                (column.querySelector('.card-header') &&
                    column.querySelector('.card-header').textContent.toLowerCase().includes('frecuencia'))
            );

            // ✅ DETECCIÓN AMPLIADA DE GRÁFICOS
            const hasChart = !!(
                column.querySelector('.chart-container') ||
                column.querySelector('.echarts-container') ||
                column.querySelector('.recharts-container') ||
                column.querySelector('.victory-container') ||
                column.querySelector('.highcharts-container') ||
                column.querySelector('[class*="vehicle"]') ||
                column.querySelector('[class*="alerta"]') ||
                column.querySelector('[data-component="chart"]') ||
                hasCanvas ||
                hasAlertasDetonadas ||
                hasAlertaFrecuencia
            );

            // ✅ DETECCIÓN DE MAPAS
            const hasMap = !!(
                column.querySelector('[id*="map"]') ||
                column.querySelector('.map-container') ||
                column.querySelector('.leaflet-container') ||
                column.querySelector('.leaflet-map-pane') ||
                column.querySelector('.leaflet-control-container') ||
                column.querySelector('.google-map') ||
                column.querySelector('.mapbox-map') ||
                column.querySelector('[class*="ubicacion"]') ||
                column.querySelector('[class*="location"]') ||
                column.querySelector('[data-component="map"]')
            );

            // ✅ DETECCIÓN DE TABLAS
            const hasTable = !!(
                column.querySelector('table') ||
                column.querySelector('.table') ||
                column.querySelector('[role="table"]') ||
                column.querySelector('.list-striped') ||
                column.querySelector('.tanstack-table') ||
                column.querySelector('[data-component="table"]')
            );

            // ✅ LÓGICA MEJORADA PARA hasVisualContent
            const hasVisualContent = hasCanvas || hasChart || hasMap || hasAlertasDetonadas || hasAlertaFrecuencia;
            const isTableOnly = hasTable && !hasVisualContent;

            // ✅ DESCRIPCIÓN MEJORADA
            let description = 'Componente visual';

            if (hasMap) {
                description = 'Mapa';
            } else if (hasAlertasDetonadas) {
                description = 'Alertas Detonadas';
            } else if (hasAlertaFrecuencia) {
                description = 'Análisis de Frecuencia';
            } else if (hasChart || hasCanvas) {
                description = 'Gráfico';
            } else if (isTableOnly) {
                description = 'Tabla';
            }

            console.log(`🔍 Resultado detección:`, {
                hasCanvas,
                hasChart,
                hasMap,
                hasTable,
                hasAlertasDetonadas,
                hasAlertaFrecuencia,
                hasVisualContent,
                isTableOnly,
                description,
                columnText: column.textContent.substring(0, 100) + '...'
            });

            return {
                hasVisualContent: hasVisualContent,
                isTable: isTableOnly,
                hasCanvas,
                hasChart,
                hasMap,
                hasTable,
                hasAlertasDetonadas,
                hasAlertaFrecuencia,
                description
            };

        } catch (error) {
            console.error('Error detectando tipo de contenido:', error);
            return {
                hasVisualContent: false,
                isTable: false,
                description: 'Contenido no identificado'
            };
        }
    }

    detectContentType_respaldo(column) {
        try {
            //const hasCanvas = !!column.querySelector('canvas');
            const hasCanvas = !!(
                column.querySelector('canvas') ||
                column.querySelector('svg') ||  // ✅ SVG charts
                column.querySelector('[data-recharts]') ||  // ✅ Recharts
                column.querySelector('.recharts-wrapper') ||  // ✅ Recharts wrapper
                column.querySelector('.chart-wrapper') ||  // ✅ Chart wrapper genérico
                column.querySelector('[class*="chart"]') ||  // ✅ Cualquier clase con "chart"
                column.querySelector('[id*="chart"]')  // ✅ Cualquier ID con "chart"
            );
            //const hasChart = !!column.querySelector('.chart-container, .echarts-container');
            const hasChart = !!(
                column.querySelector('.chart-container') ||
                column.querySelector('.echarts-container') ||
                column.querySelector('.recharts-container') ||  // ✅ Recharts
                column.querySelector('.victory-container') ||  // ✅ Victory charts
                column.querySelector('.highcharts-container') ||  // ✅ Highcharts
                column.querySelector('[class*="vehicle"]') ||  // ✅ Componente AlertaVehiculo
                column.querySelector('[class*="alerta"]') ||  // ✅ Componentes de alertas
                column.querySelector('[data-component="chart"]') ||  // ✅ Data attribute
                hasCanvas  // ✅ Si tiene canvas, es un gráfico
            );

            //const hasMap = !!column.querySelector('[id*="map"], .map-container');
            const hasMap = !!(
                column.querySelector('[id*="map"]') ||
                column.querySelector('.map-container') ||
                column.querySelector('.leaflet-container') ||  // ✅ Leaflet maps
                column.querySelector('.leaflet-map-pane') ||  // ✅ Leaflet pane
                column.querySelector('.leaflet-control-container') ||  // ✅ Leaflet controls
                column.querySelector('.google-map') ||
                column.querySelector('.mapbox-map') ||  // ✅ Mapbox
                column.querySelector('[class*="ubicacion"]') ||  // ✅ Componente AlertaUbicacion
                column.querySelector('[class*="location"]') ||  // ✅ Location components
                column.querySelector('[data-component="map"]')  // ✅ Data attribute
            );
            //const hasTable = !!column.querySelector('table, .table, [role="table"], .list-striped');
            const hasTable = !!(
                column.querySelector('table') ||
                column.querySelector('.table') ||
                column.querySelector('[role="table"]') ||
                column.querySelector('.list-striped') ||
                column.querySelector('.tanstack-table') ||  // ✅ TanStack table
                column.querySelector('[data-component="table"]')  // ✅ Data attribute
            );

            const hasVisualContent = hasCanvas || hasChart || hasMap;
            const isTableOnly = hasTable && !hasVisualContent;

            let description = 'Componente visual';

            if (hasMap) {
                description = 'Mapa';  // ✅ Simple, sin "interactivo"
            } else if (hasChart || hasCanvas) {
                description = 'Gráfico';  // ✅ Simple, sin especificar tipo
            } else if (isTableOnly) {
                description = 'Tabla';  // ✅ Simple, sin "de datos"
            }
            // if (hasMap) {
            //     description = 'Mapa interactivo';
            // } else if (hasChart || hasCanvas) {
            //     // 🎯 DETECCIÓN ESPECÍFICA POR CONTENIDO
            //     const text = column.textContent.toLowerCase();
            //     if (text.includes('vehículo') || text.includes('vehicle')) {
            //         description = 'Gráfico de vehículos';
            //     } else if (text.includes('alerta') || text.includes('alert')) {
            //         description = 'Gráfico de alertas';
            //     } else if (text.includes('frecuencia') || text.includes('frequency')) {
            //         description = 'Gráfico de frecuencia';
            //     } else {
            //         description = 'Gráfico de análisis';
            //     }
            // } else if (isTableOnly) {
            //     description = 'Tabla de datos';
            // }

            console.log('🔍 Detectando contenido en columna:', {
                hasCanvas,
                hasChart,
                hasMap,
                hasTable,
                hasVisualContent,
                isTableOnly,
                description,
                columnClasses: column.className,
                columnId: column.id
            });

            return {
                hasVisualContent: hasVisualContent,
                isTable: isTableOnly,
                hasCanvas,
                hasChart,
                hasMap,
                hasTable,
                description
            };

        } catch (error) {
            console.error('Error detectando tipo de contenido:', error);
            return {
                hasVisualContent: false,
                isTable: false,
                description: 'Contenido no identificado'
            };
        }
    }

    getColumnTitle(column, identifier, tipoContenido) {
        try {
            // 🎯 BUSCAR SOLO TÍTULOS ORIGINALES
            const titleSelectors = [
                '.card-header h6',
                '.card-header h5',
                '.card-header h4',
                '.card-title',
                'h6', 'h5', 'h4', 'h3'
            ];

            for (const selector of titleSelectors) {
                const titleEl = column.querySelector(selector);
                if (titleEl && titleEl.textContent.trim()) {
                    const title = titleEl.textContent.trim();
                    if (TextUtils.isValidTitle(title)) {
                        return title;
                    }
                }
            }

            // ✅ Si no encuentra título, usar solo el identificador
            return `Sección ${identifier}`;

        } catch (error) {
            console.error('Error obteniendo título:', error);
            return `Sección ${identifier}`;
        }
    }

    // getColumnTitle(column, identifier, tipoContenido) {
    //     try {
    //         // Buscar título dentro de la columna
    //         const titleSelectors = [
    //             '.card-header h6',
    //             '.card-title',
    //             'h6', 'h5', 'h4', 'h3',
    //             '.title',
    //             '[data-title]'
    //         ];

    //         for (const selector of titleSelectors) {
    //             const titleEl = column.querySelector(selector);
    //             if (titleEl && titleEl.textContent.trim()) {
    //                 const title = titleEl.textContent.trim();
    //                 if (TextUtils.isValidTitle(title)) {
    //                     return title;
    //                 }
    //             }
    //         }

    //         // Títulos específicos según contenido
    //         if (tipoContenido.hasMap) {
    //             return 'Ubicaciones en Mapa';
    //         }

    //         if (tipoContenido.hasChart || tipoContenido.hasCanvas) {
    //             const text = column.textContent.toLowerCase();
    //             if (text.includes('vehículo')) return 'Análisis por Vehículo';
    //             if (text.includes('alerta')) return 'Alertas Detonadas';
    //             if (text.includes('frecuencia')) return 'Análisis de Frecuencia';
    //             return 'Gráfico de Análisis';
    //         }

    //         return `Análisis ${identifier.replace('fila-', '').replace('-col-', ' columna ')}`;

    //     } catch (error) {
    //         console.error('Error obteniendo título de columna:', error);
    //         return 'Componente de Análisis';
    //     }
    // }


    /**
 * ✅ MEJORADO: Esperar a que los componentes estén completamente renderizados
 */
    async waitForComponentsToBeReady(element, identifier) {
        console.log(`⏳ Verificando componentes para ${identifier}...`);

        let attempts = 0;
        const maxAttempts = 25; // Aumentar intentos

        while (attempts < maxAttempts) {
            try {
                // ✅ VERIFICAR DIMENSIONES DEL ELEMENTO PRINCIPAL
                if (!this.hasValidDimensions(element)) {
                    console.log(`📐 ${identifier}: Esperando dimensiones válidas (${attempts})`);
                    await this.delay(400);
                    attempts++;
                    continue;
                }

                // ✅ VERIFICAR MAPAS (GoogleMap, Leaflet)
                const mapElements = element.querySelectorAll(
                    '.google-map, .leaflet-container, canvas[data-type="map"], ' +
                    '[class*="map"], [id*="map"]'
                );

                if (mapElements.length > 0) {
                    let mapReady = true;
                    for (const mapEl of mapElements) {
                        if (!this.isMapReady(mapEl)) {
                            mapReady = false;
                            break;
                        }
                    }

                    if (!mapReady) {
                        console.log(`🗺️ ${identifier}: Esperando que el mapa esté listo (${attempts})`);
                        await this.delay(600);
                        attempts++;
                        continue;
                    }
                }

                // ✅ VERIFICAR GRÁFICOS (ECharts, Canvas)
                const chartElements = element.querySelectorAll(
                    'canvas, .echarts-container, [class*="chart"], ' +
                    '.recharts-container, svg'
                );

                if (chartElements.length > 0) {
                    let chartsReady = true;
                    for (const chartEl of chartElements) {
                        if (!this.isChartReady(chartEl)) {
                            chartsReady = false;
                            break;
                        }
                    }

                    if (!chartsReady) {
                        console.log(`📊 ${identifier}: Esperando que los gráficos estén listos (${attempts})`);
                        await this.delay(500);
                        attempts++;
                        continue;
                    }
                }

                // ✅ VERIFICAR COMPONENTES REACT
                const reactComponents = element.querySelectorAll(
                    '[data-component], .smart-component, [class*="Component"]'
                );

                if (reactComponents.length > 0) {
                    let componentsReady = true;
                    for (const component of reactComponents) {
                        if (!this.isReactComponentReady(component)) {
                            componentsReady = false;
                            break;
                        }
                    }

                    if (!componentsReady) {
                        console.log(`⚛️ ${identifier}: Esperando componentes React (${attempts})`);
                        await this.delay(400);
                        attempts++;
                        continue;
                    }
                }

                // ✅ VERIFICACIÓN FINAL - ESPERA ADICIONAL PARA ESTABILIDAD
                await this.delay(300);

                console.log(`✅ ${identifier}: Todos los componentes están listos después de ${attempts} intentos`);
                return true;

            } catch (error) {
                console.warn(`⚠️ Error verificando componentes para ${identifier}:`, error);
                await this.delay(500);
                attempts++;
            }
        }

        console.warn(`⚠️ ${identifier}: Tiempo agotado esperando componentes, continuando...`);
        return false;
    }

    /**
     * ✅ NUEVO: Verificar si el elemento tiene dimensiones válidas
     */
    hasValidDimensions(element) {
        if (!element) return false;

        const rect = element.getBoundingClientRect();
        const computedStyle = window.getComputedStyle(element);

        return rect.width > 10 &&
            rect.height > 10 &&
            computedStyle.display !== 'none' &&
            computedStyle.visibility !== 'hidden' &&
            computedStyle.opacity !== '0';
    }

    /**
     * ✅ NUEVO: Verificar si el mapa está listo
     */
    isMapReady(mapElement) {
        if (!mapElement) return false;

        // Verificar dimensiones
        const rect = mapElement.getBoundingClientRect();
        if (rect.width <= 10 || rect.height <= 10) {
            return false;
        }

        // Verificar si es GoogleMap
        if (mapElement.classList.contains('google-map') ||
            mapElement.querySelector('.gm-style')) {
            const gmStyle = mapElement.querySelector('.gm-style');
            return gmStyle && gmStyle.children.length > 0;
        }

        // Verificar si es Leaflet
        if (mapElement.classList.contains('leaflet-container')) {
            const leafletPane = mapElement.querySelector('.leaflet-map-pane');
            return leafletPane && leafletPane.children.length > 0;
        }

        // Para otros tipos de mapas
        return rect.width > 100 && rect.height > 100;
    }

    /**
     * ✅ NUEVO: Verificar si el gráfico está listo
     */
    isChartReady(chartElement) {
        if (!chartElement) return false;

        const rect = chartElement.getBoundingClientRect();

        // Verificar dimensiones básicas
        if (rect.width <= 10 || rect.height <= 10) {
            return false;
        }

        // Para canvas
        if (chartElement.tagName === 'CANVAS') {
            // Verificar que el canvas tenga contenido
            try {
                const context = chartElement.getContext('2d');
                const imageData = context.getImageData(0, 0, 1, 1);
                return chartElement.width > 0 && chartElement.height > 0;
            } catch (error) {
                return false;
            }
        }

        // Para ECharts
        if (chartElement.classList.contains('echarts-container') ||
            chartElement.querySelector('.echarts-container')) {
            const canvas = chartElement.querySelector('canvas');
            return canvas && canvas.width > 0 && canvas.height > 0;
        }

        // Para SVG
        if (chartElement.tagName === 'SVG') {
            return chartElement.children.length > 0;
        }

        return true;
    }

    /**
     * ✅ NUEVO: Verificar si el componente React está listo
     */
    isReactComponentReady(component) {
        if (!component) return false;

        const rect = component.getBoundingClientRect();

        // Verificar dimensiones
        if (rect.width <= 10 || rect.height <= 10) {
            return false;
        }

        // Verificar que no esté en estado de carga
        const loadingIndicators = component.querySelectorAll(
            '.spinner-border, .skeleton, .loading, [class*="loading"]'
        );

        if (loadingIndicators.length > 0) {
            // Verificar si realmente están visibles
            for (const loading of loadingIndicators) {
                const loadingRect = loading.getBoundingClientRect();
                if (loadingRect.width > 0 && loadingRect.height > 0) {
                    return false;
                }
            }
        }

        return true;
    }

    /**
     * ✅ MEJORADO: Captura de fila completa para recorridos con mejor manejo de errores
     */
    async captureFilaCompletaRecorridos(filaElement, identifier, titulo, sections) {
        try {
            console.log(`📸 Intentando capturar fila: ${titulo}`);

            if (!filaElement) {
                console.warn(`❌ No se encontró elemento para fila: ${titulo}`);
                return;
            }

            if (!this.isElementVisible(filaElement)) {
                console.warn(`❌ Fila no visible: ${titulo}`);
                return;
            }

            console.log(`✅ Fila encontrada: ${titulo}`);

            // ✅ ESPERAR A QUE TODOS LOS COMPONENTES ESTÉN LISTOS
            await this.waitForComponentsToBeReady(filaElement, titulo);

            // ✅ ESPERA MÍNIMA ADICIONAL PARA ESTABILIDAD
            console.log(`⏳ Espera mínima para: ${titulo}`);
            await this.delay(800); // Aumentar espera

            // ✅ PREPARAR ELEMENTO PARA CAPTURA
            await this.prepareElementForCapture(filaElement);

            let canvas = null;
            let attempts = 0;
            const maxAttempts = 3;

            // ✅ INTENTOS MÚLTIPLES DE CAPTURA
            while (attempts < maxAttempts && !canvas) {
                try {
                    console.log(`🎯 Intento ${attempts + 1} de captura para: ${titulo}`);

                    canvas = await this.captureElement(filaElement, identifier);

                    if (canvas && canvas.width > 0 && canvas.height > 0) {
                        console.log(`✅ Fila capturada exitosamente: ${titulo} (${canvas.width}x${canvas.height})`);
                        break;
                    } else {
                        console.warn(`⚠️ Canvas inválido en intento ${attempts + 1} para: ${titulo}`);
                        canvas = null;
                    }

                } catch (error) {
                    console.error(`❌ Error en intento ${attempts + 1} para ${titulo}:`, error);
                    canvas = null;
                }

                attempts++;
                if (attempts < maxAttempts) {
                    console.log(`⏳ Esperando antes del siguiente intento...`);
                    await this.delay(1000);
                }
            }

            if (canvas && canvas.width > 0 && canvas.height > 0) {
                sections.push({
                    canvas,
                    title: titulo,
                    description: `Fila completa: ${titulo}`,
                    order: sections.length + 1,
                    type: 'row',
                    identifier
                });

                console.log(`✅ Fila agregada a secciones: ${titulo}`);
            } else {
                console.error(`❌ No se pudo capturar la fila: ${titulo}`);
            }

        } catch (error) {
            console.error(`❌ Error general capturando fila ${titulo}:`, error);
        } finally {
            try {
                await this.restoreElementAfterCapture(filaElement);
            } catch (restoreError) {
                console.warn(`⚠️ Error restaurando elemento:`, restoreError);
            }
        }
    }

    /**
     * ✅ MEJORADO: Delay con mejor manejo
     */
    async delay(ms) {
        return new Promise(resolve => {
            setTimeout(resolve, Math.max(ms, 100)); // Mínimo 100ms
        });
    }

}

export default PrintReportModule;
